/* eslint-disable jsx-a11y/label-has-associated-control */
import React from 'react';
import type { IconButtonProps } from '@mui/material/IconButton';
import IconButton from '@mui/material/IconButton';
import { useField } from 'formik';

import { useTranslation } from 'next-i18next';

import Icon from 'src/components/ui/Icon';

import useAlerts from 'src/hooks/useAlerts';

import { colours } from 'src/constants/colours';
import {
    imageMimes,
    maximumImageSize,
    maximumVideoSize,
    videoMimes,
    maximumFilesCount,
} from 'src/constants/fileuploads';
import readAsDataURL from 'src/utils/readAsDataURL';
import bitsToMegaBytes from 'src/utils/bitsToMegaBytes';
import { styledComponent } from 'src/utils/styled';

interface StyledIconButtonProps extends IconButtonProps {
    component?: string;
}
export const StyledIconButton = styledComponent(IconButton)<StyledIconButtonProps>`
    border: 0.0625rem solid ${colours.iconButtonBorder};
    border-radius: 2.5rem;
    height: 2.5rem;
    width: 2.5rem;
    .MuiIconButton-label {
        width: fit-content;
    }
    svg {
        cursor: pointer !important;
    }
`;

type Props = {
    type: MediaTypes;
    inputRef?: (node: unknown) => void;
};

const PostFileInput: React.FC<Props> = ({ type, inputRef }) => {
    const { t } = useTranslation(['common']);
    const [field, meta, helpers] = useField('media');
    const { setValue, setTouched } = helpers;
    const { addAlert } = useAlerts();
    const value = field.value as unknown[];

    const addSRCOnFileObject = async (file: File) => {
        const src = await readAsDataURL(file);
        return {
            file,
            src,
        };
    };
    const handleAddFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files ? Array.from(event.target.files) : undefined;

        if (files) {
            // Validate totalFilesCount
            const totalFilesCount = value.length + files.length;
            if (totalFilesCount > maximumFilesCount) {
                addAlert({
                    type: 'error',
                    message: t('common:errors.maxFiles', { count: maximumFilesCount }),
                });
                return;
            }

            // Validate file type
            const acceptedMimes = type === 'image' ? imageMimes : videoMimes;
            if (files.find((file: File) => !acceptedMimes.includes(file.type))) {
                addAlert({
                    type: 'error',
                    message: t('common:imageInput.fileTypeError'),
                });
                return;
            }

            // Validate file size
            const maxSize = type === 'image' ? maximumImageSize : maximumVideoSize;
            if (files.find((file: File) => file.size > maxSize)) {
                addAlert({
                    type: 'error',
                    message: t('common:imageInput.fileSizeError', {
                        size: bitsToMegaBytes(maxSize),
                    }),
                });
                return;
            }
            const filesPromises = files.map((file) => addSRCOnFileObject(file));
            const filesWithSrc = await Promise.all(filesPromises);
            setValue([...value, ...filesWithSrc]);
        }
        setTouched(true);
    };

    return (
        <>
            <label htmlFor={`${type}-input-modal`} id={`${type}-label-modal`}>
                <StyledIconButton name={type === 'image' ? 'Projects' : 'Video'} component="span">
                    <Icon name={type === 'image' ? 'Projects' : 'Video'} size="1.5rem" />
                </StyledIconButton>
            </label>
            <input
                ref={inputRef}
                id={`${type}-input-modal`}
                name={`${type}-input-modal`}
                type="file"
                accept={type === 'image' ? imageMimes.join(',') : videoMimes.join(',')}
                onChange={async (e) => handleAddFile(e)}
                hidden
                multiple
            />
        </>
    );
};

export default PostFileInput;
